// lib/pipeline-watch/monitored-fields.ts
//
// Tripwire normalization layer: raw CTGov v2 study JSON -> the monitored
// field subset diffs run on, the PII strip that keeps the archive
// personal-data-free by construction (Decision Log 2026-07-13), and the
// content hash that decides whether a snapshot is written at all.
//
// The hash covers the FULL stripped protocolSection (including the annual
// verification/lastUpdate dates) on purpose: administrative refreshes must
// produce a snapshot + an `administrative` event so the digest's trust line
// ("N routine updates filtered out") can count them. The DIFF layer is what
// ignores those fields, not the hash.

import { createHash } from "crypto";

export type DatedField = {
  date: string | null;
  type: string | null; // "ACTUAL" | "ESTIMATED" | "ANTICIPATED" | null
};

/**
 * A trial outcome (endpoint). `measure` is the endpoint name, `timeFrame`
 * its assessment window. The full `description` is deliberately NOT captured:
 * it reflows constantly (formatting, boilerplate) and diffing it would flag
 * noise. The endpoint's IDENTITY is measure + timeFrame ([[Change Detection
 * Depth — Plan]] E1).
 */
export type Outcome = {
  measure: string | null;
  timeFrame: string | null;
};

export type MonitoredFields = {
  nctId: string | null;
  briefTitle: string | null;
  /** Registry acronym (e.g. "SURPASS-2"); null when the sponsor left it blank. */
  acronym: string | null;
  /**
   * Sponsor's own protocol / org study ID (e.g. "COVALENT-212"). Distinct from
   * `acronym` — many records fill this and leave acronym blank. Used as the
   * under-title short label when acronym is absent.
   */
  orgStudyId: string | null;
  overallStatus: string | null;
  startDate: DatedField;
  primaryCompletionDate: DatedField;
  completionDate: DatedField;
  enrollment: { count: number | null; type: string | null };
  phases: string[];
  leadSponsor: string | null;
  /**
   * Primary endpoints. A change here (added / removed / measure-or-timeframe
   * changed) is one of the most significant protocol amendments there is —
   * it changes what the trial MEASURES. Diffed deterministically; primary =
   * critical, secondary = notable ([[Change Detection Depth — Plan]] E1).
   */
  primaryOutcomes: Outcome[];
  /** Secondary endpoints — same shape, tiered lower. */
  secondaryOutcomes: Outcome[];
  /**
   * When the trial first POSTED its results to ClinicalTrials.gov (the
   * outcome data going public — the single biggest catalyst). null until
   * posted. Same {date,type} shape as the other dated fields; a null -> date
   * transition is the "results posted" event ([[Change Detection Depth —
   * Plan]] E2). NB: the results DATA (resultsSection) is stripped and never
   * stored — we surface the EVENT, not the numbers.
   */
  resultsFirstPostDate: DatedField;
  /**
   * The sponsor's stated reason a trial was stopped, verbatim from the
   * registry ("terminated: lack of efficacy" vs "business decision" vs
   * "safety"). null when the trial is not stopped. Rides the terminate/
   * suspend status flip; the distinction between a drug failing and a
   * portfolio reshuffle ([[Change Detection Depth — Plan]] E3).
   */
  whyStopped: string | null;
  /** Metadata only — excluded from diffs (it moves on every record touch). */
  lastUpdatePostDate: string | null;
  /**
   * When the study first appeared on the registry. Metadata only (immutable
   * per trial, never diffed) — the new_trial classifier uses it to require
   * that a "new" trial is actually new, not an old trial rotating into a
   * drug's scope window (the 796-phantom-event lesson, 2026-07-15).
   */
  studyFirstPostDate: string | null;
};

type RawStudy = Record<string, unknown>;

function get(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const key of path) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function datedField(v: unknown): DatedField {
  return {
    date: str(get(v, ["date"])),
    type: str(get(v, ["type"])),
  };
}

/** Normalize an outcomes array to {measure, timeFrame} entries, order
 *  preserved (a reorder is not a change; add/remove/edit is). */
function outcomes(v: unknown): Outcome[] {
  if (!Array.isArray(v)) return [];
  return v.map((o) => ({
    measure: str(get(o, ["measure"])),
    timeFrame: str(get(o, ["timeFrame"])),
  }));
}

export function normalizeStudy(raw: RawStudy): MonitoredFields {
  const p = get(raw, ["protocolSection"]);
  const status = get(p, ["statusModule"]);
  const design = get(p, ["designModule"]);
  const outcomesMod = get(p, ["outcomesModule"]);
  const phasesRaw = get(design, ["phases"]);
  return {
    nctId: str(get(p, ["identificationModule", "nctId"])),
    briefTitle: str(get(p, ["identificationModule", "briefTitle"])),
    acronym: str(get(p, ["identificationModule", "acronym"])),
    orgStudyId: str(get(p, ["identificationModule", "orgStudyIdInfo", "id"])),
    overallStatus: str(get(status, ["overallStatus"])),
    startDate: datedField(get(status, ["startDateStruct"])),
    primaryCompletionDate: datedField(get(status, ["primaryCompletionDateStruct"])),
    completionDate: datedField(get(status, ["completionDateStruct"])),
    enrollment: {
      count: num(get(design, ["enrollmentInfo", "count"])),
      type: str(get(design, ["enrollmentInfo", "type"])),
    },
    phases: Array.isArray(phasesRaw)
      ? phasesRaw.filter((x): x is string => typeof x === "string")
      : [],
    leadSponsor: str(
      get(p, ["sponsorCollaboratorsModule", "leadSponsor", "name"]),
    ),
    primaryOutcomes: outcomes(get(outcomesMod, ["primaryOutcomes"])),
    secondaryOutcomes: outcomes(get(outcomesMod, ["secondaryOutcomes"])),
    resultsFirstPostDate: datedField(get(status, ["resultsFirstPostDateStruct"])),
    whyStopped: str(get(status, ["whyStopped"])),
    lastUpdatePostDate: str(get(status, ["lastUpdatePostDateStruct", "date"])),
    studyFirstPostDate: str(get(status, ["studyFirstPostDateStruct", "date"])),
  };
}

/**
 * Strip personal data before storage: central contacts, overall officials
 * (named investigators), and per-location contacts/investigators. Facility,
 * city, and country survive (site geography is analytic signal, not PII).
 * Returns a deep-cloned study; never mutates the input.
 */
export function stripContactPii(raw: RawStudy): RawStudy {
  const clone = JSON.parse(JSON.stringify(raw)) as RawStudy;
  const p = clone["protocolSection"];
  if (p !== null && typeof p === "object") {
    const contacts = (p as Record<string, unknown>)["contactsLocationsModule"];
    if (contacts !== null && typeof contacts === "object") {
      const c = contacts as Record<string, unknown>;
      delete c["centralContacts"];
      delete c["overallOfficials"];
      const locations = c["locations"];
      if (Array.isArray(locations)) {
        for (const loc of locations) {
          if (loc !== null && typeof loc === "object") {
            delete (loc as Record<string, unknown>)["contacts"];
          }
        }
      }
    }
  }
  return clone;
}

/**
 * Deterministic stringify: object keys sorted at every depth (array order is
 * preserved). Exported so the change classifier can decide "which modules
 * differ" with the SAME comparison the content hash uses — otherwise a
 * key-order-sensitive diff (JSON.stringify) flags modules that did not
 * actually change, because the stored copy (Postgres jsonb key order) and the
 * fresh CTGov fetch order their keys differently (2026-07-16 routine-wall bug).
 */
export function canonicalize(v: unknown): string {
  if (Array.isArray(v)) {
    return `[${v.map(canonicalize).join(",")}]`;
  }
  if (v !== null && typeof v === "object") {
    const keys = Object.keys(v as Record<string, unknown>).sort();
    return `{${keys
      .map((k) => `${JSON.stringify(k)}:${canonicalize((v as Record<string, unknown>)[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v) ?? "null";
}

/** Hash the stripped protocolSection — the "did anything change" fingerprint. */
export function contentHash(strippedRaw: RawStudy): string {
  const section = get(strippedRaw, ["protocolSection"]) ?? strippedRaw;
  return createHash("sha256").update(canonicalize(section)).digest("hex");
}

/**
 * CTGov dates are "YYYY", "YYYY-MM", or "YYYY-MM-DD". Month precision is the
 * norm; missing parts default to the 1st so deltas are computable.
 */
export function parseCtgovDate(date: string | null): Date | null {
  if (!date) return null;
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(date);
  if (!m) return null;
  const year = Number(m[1]);
  const month = m[2] ? Number(m[2]) - 1 : 0;
  const day = m[3] ? Number(m[3]) : 1;
  const d = new Date(Date.UTC(year, month, day));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function deltaDays(from: string | null, to: string | null): number | null {
  const a = parseCtgovDate(from);
  const b = parseCtgovDate(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/**
 * Under-title short label for trial lists: the registry acronym, or null.
 *
 * Deliberately does NOT fall back to `orgStudyId` (founder call 2026-07-25).
 * Sponsor protocol IDs are frequently opaque strings like `D5087C00001`, and
 * this label feeds the PUBLIC trial-pipeline page as well as the member
 * tracker, where a machine identifier standing in for a trial's name reads as
 * noise rather than intelligence. `orgStudyId` is still extracted and still
 * shown in the full trial record, where it is explicitly labelled as the org
 * study ID rather than substituted for a name.
 */
export function trialShortLabel(
  m: Pick<MonitoredFields, "acronym" | "orgStudyId">,
): string | null {
  return m.acronym ?? null;
}

export const _internals = { canonicalize, get, datedField };
